import { PageTable } from '../../components-common/layout';
import {
  ConfigSection,
  EditTemplate,
  IndexTemplate,
} from '../../components-fabric';
import {
  CourseForm,
  getCourseValidationSchema,
} from '../../components-domain/education-domain/courses';
import { clone } from 'ramda';
import { layoutCourse } from '../../_website/_common/layout.course';
import { courseTabs } from './tabs';
import { courseResourcesScreens } from './resources';
import { courseMembersScreens } from './members';
import { courseAssignmentsScreens } from './assignments';
import { layoutWorkspace } from '../_common/layout.workspace';
import { DashboardPageCourses } from './dashboard-courses/DashboardPageCourses';
import { PageFormToolbar } from '../../components-common/layout/views/PageFormToolbar';
import { addDaysToDate } from '../_helper.functions';
import { courseGroupsScreens } from './groups';

/**
 * Configuration Object: indexScreen
 *
 * This object contains the configuration for the index screen of courses.
 * It includes columns, actions, roles, and layout information.
 */
const indexScreen: IndexTemplate = {
  id: 'index',
  path: '.',
  roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
  Component: PageTable,
  title: 'Courses',
  tmp: 'index',
  columns: [
    'courseLogo',
    'number',
    'name',
    'lecturer',
    'state',
    'startDate',
    'endDate',
  ],
  left: [
    {
      id: 'create_new',
      kind: 'create_new',
      roles: ['STUDY_DIRECTOR', 'LECTURER'],
    } as any,
  ],
  actions: [
    {
      id: 'view',
      onClick: (props: any) => {
        if (props.userRole === 'STUDENT')
          props.navigate(`/courses/${props.row.id}/resources`);
        else props.navigate(`/courses/${props.row.id}`);
      },
      displayTooltip: true,
      tooltipText: 'View course',
    },
    {
      id: 'edit',
      roles: ['STUDY_DIRECTOR', 'LECTURER'],
      actionToDispatch: { payload: { path: 'edit' } },
      displayTooltip: true,
      tooltipText: 'Edit course',
    } as any,
    {
      id: 'copy',
      roles: ['STUDY_DIRECTOR', 'LECTURER'],
      actionToDispatch: {
        payload: {
          copy: '/coursesms/courses/courseClone',
        },
      },
      displayTooltip: true,
      tooltipText: 'Duplicate course',
    } as any,
    {
      id: 'delete',
      roles: ['STUDY_DIRECTOR', 'LECTURER'],
      actionToDispatch: {
        payload: {
          endpointToDelete: '/coursesms/courses',
        },
      },
      displayTooltip: true,
      tooltipText: 'Delete course',
    },
  ],
  layout: layoutWorkspace,
  source: '/coursesms/courses',
};

/**
 * Configuration Object: dashboardScreen
 *
 * This object contains the configuration for the dashboard screen of courses.
 * It includes roles, layout, and component information.
 */
const dashboardScreen: any = {
  id: 'dashboard',
  path: '',
  protected: false,
  roles: ['STUDY_DIRECTOR', 'LECTURER'],
  tmp: 'index',
  layout: layoutWorkspace,
  Component: DashboardPageCourses,
};

/**
 * Configuration Object: formScreen
 *
 * This object contains the configuration for the form screen of courses.
 * It includes the form component, schema, and default values for creating or editing a course.
 */
const formScreen = {
  Component: PageFormToolbar,
  form: CourseForm,
  template: '/coursesms/courses',
  schema: getCourseValidationSchema(),
  empty: {
    id: '',
    number: '',
    name: '',
    description: '',
    state: 'ACTIVE',
    startDate: addDaysToDate(new Date(Date.now()), 1),
    endDate: addDaysToDate(new Date(Date.now()), 2),
    picture: {
      url: '',
    },
    contents: '',
    fileName: '',
  },
};

/**
 * Configuration Object: createScreen
 *
 * This object contains the configuration for the create screen of courses.
 * It includes roles, layout, and component information.
 */
const createScreen: any = {
  ...clone(formScreen),
  left: ['create'],
  id: 'create',
  path: 'new',
  roles: ['STUDY_DIRECTOR', 'LECTURER'],
  tmp: 'create',
  layout: layoutWorkspace,
};

/**
 * Configuration Object: editScreen
 *
 * This object contains the configuration for the edit screen of courses.
 * It includes roles, layout, and component information.
 */
const editScreen: EditTemplate = {
  ...clone(formScreen),
  left: ['save'],
  right: ['back'],
  id: 'edit',
  path: ':id/edit',
  editSource: '/coursesms/courses/',
  roles: ['STUDY_DIRECTOR', 'LECTURER'],
  tmp: 'edit',
  layout: layoutWorkspace,
  tabs: [...courseTabs],
} as any;

/**
 * Configuration Object: courseScreens
 *
 * This object contains the configuration for the course screens.
 * It includes nested sections for dashboard, members, resources, and assignments.
 */
export const courseScreens: ConfigSection = {
  id: 'courses',
  path: 'courses',
  layout: layoutWorkspace,
  screens: [indexScreen, createScreen, editScreen],
  sections: [
    {
      id: 'course',
      path: ':id',
      screens: [dashboardScreen, ...courseMembersScreens],
      sections: [courseGroupsScreens, courseResourcesScreens, courseAssignmentsScreens],
    },
  ],
};
